import { Component, OnInit, signal, Input, Output, EventEmitter, computed } from '@angular/core';
import { PublicStylesApiService } from '../../../../core/api/projects/public-styles-api.service';
import { MainStyleDto } from '../../../../core/api/_dtos/projects.dto';
import { AppTag } from '../../../../features/client/components/atom/app-tag/app-tag';

@Component({
  selector: 'app-style-picker',
  imports: [AppTag],
  templateUrl: './style-picker.html',
  styleUrl: './style-picker.scss'
})
export class StylePicker implements OnInit {
  @Input() type: 'preview' | 'project' = 'preview'
  @Input() selectionMode: 'hover' | 'click' | 'both' = 'hover';
  @Input() productId: string | null = null;
  @Input() waitForProduct = false; // If true, don't load until productId is set
  @Input() initialStyleId: string | null = null;
  @Output() selectedStyleChange = new EventEmitter<MainStyleDto | null>();
  selectedStyle = signal<MainStyleDto | null>(null)
  categoryFilter = signal<string | null>(null)
  styles = signal<MainStyleDto[]>([]);
  clickedStyle: MainStyleDto | null = null

  // Dynamically get unique tags from styles
  styleCategories = computed(() => {
    const tags = this.styles()
      .map(s => s.tag?.toLowerCase())
      .filter((tag): tag is string => !!tag);
    return [...new Set(tags)];
  });

  private lastLoadedProductId: string | null | undefined = undefined;

  constructor(
    private api: PublicStylesApiService
  ) {}

  ngOnInit(): void {
    // Load immediately if not waiting for product, or if productId is already set
    if (!this.waitForProduct || this.productId) {
      this.loadStyles();
    }
  }

  /** Called from parent component when productId changes (signal-based) */
  onProductIdChange(productId: string | null): void {
    if (this.lastLoadedProductId === productId) return;
    if (this.waitForProduct && !productId) return;

    this.productId = productId;
    this.loadStyles();
  }

  selectStyle(style: MainStyleDto | null): void {
    this.selectedStyle.set(style);
    this.selectedStyleChange.emit(style);
  }

  onStyleHover(style: MainStyleDto): void {
    if (this.selectionMode === 'hover' || this.selectionMode === 'both') {
      this.selectStyle(style);
    }
  }

  onStyleClick(style: MainStyleDto): void {
    if (this.selectionMode === 'click' || this.selectionMode === 'both') {
      this.selectStyle(style);
      this.clickedStyle = style;
    }
  }

  onMouseLeave(): void {
    if (this.selectionMode === 'hover' || this.selectionMode === 'both') {
      this.selectedStyle.set(this.clickedStyle);
      this.selectedStyleChange.emit(this.clickedStyle);
    }
  }

  onStyleItemLeave(style: MainStyleDto): void {
    if (this.selectionMode === 'hover' || this.selectionMode === 'both') {
      // Only clear if the style that was left is still the selected one
      if (this.selectedStyle()?.id === style.id) {
        this.selectedStyle.set(null);
        this.selectedStyleChange.emit(null);
      }
    }
  }

  selectCategoryFilter(category: string): void {
    const newCategoryFilter = this.categoryFilter() === category ? null : category;
    this.categoryFilter.set(newCategoryFilter);
  }

  filterStylesByCategory(): MainStyleDto[] {
    const currentFilter = this.categoryFilter();
    if (!currentFilter) {
      return this.styles();
    } else {
      return this.styles().filter(s => s.tag?.toLowerCase() === currentFilter)
    }
  }

  private loadStyles(): void {
    this.api.getStyles(this.productId ?? undefined).subscribe(styles => {
      this.styles.set(styles.data);
      this.tryPreSelectInitialStyle(styles.data);
    });
  }

  private tryPreSelectInitialStyle(styles: MainStyleDto[]): void {
    if (!this.initialStyleId || this.clickedStyle) return;
    const match = styles.find(s => s.id === this.initialStyleId);
    if (match) {
      this.selectStyle(match);
      this.clickedStyle = match;
      // Clear so it doesn't re-trigger on subsequent loads
      this.initialStyleId = null;
    }
  }
}
